/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from "react-router-dom";
import Select from 'react-select'
import { Button, Card, Spinner, Table } from 'react-bootstrap'
import { skipToken } from '@reduxjs/toolkit/query'
import DataTable from 'components/atoms/DataTable'
import { TableWrapper } from 'components/atoms/TableWrapper'
import moment from 'moment'

import { downloadBlob } from 'helpers/generalHelper'
import {
  useExportPatientAgingReportMutation,
  useGetPatientAgingReportQuery,
} from 'store/services/managementReports'
import { PatientAgingType } from 'store/types'

const typeOptions = [
  { label: 'HST', value: 'HST' as PatientAgingType },
  { label: 'OneShip', value: 'ONESHIP' as PatientAgingType },
  { label: 'Follow-Up Care', value: 'FOLLOW_UP_CARE' as PatientAgingType },
]

const hstStatusOptions = [
  { label: 'Verification', value: 'HST_VERIFICATION' },
  { label: 'Patient Consent', value: 'HST_PATIENT_CONSENT' },
  { label: 'Pre Auth', value: 'HST_AUTHORIZATION' },
  { label: 'Patient Scheduling', value: 'HST_PATIENT_SCHEDULING' },
  { label: 'Need to Ship', value: 'NO_NEED_TO_SHIP' },
  { label: 'Ready to Ship', value: 'NO_READY_TO_SHIP' },
  { label: 'Shipped to Patient', value: 'NO_SHIPPED' },
  { label: 'Patient Testing', value: 'HST_PATIENT_TESTING' },
  { label: 'Pending Return', value: 'PENDING_RETURN' },
  { label: 'Shipped to TestSmarter', value: 'NO_SHIPPED_BACK' },
  { label: 'Report Pending', value: 'HST_PENDING_REPORT' },
]

const oneShipStatusOptions = [
  { label: 'New', value: 'OPEN' },
  { label: 'Ready to Schedule', value: 'NO_NEW' },
  { label: 'Follow Up', value: 'NO_FOLLOW_UP' },
  { label: 'Need to Ship', value: 'NO_NEED_TO_SHIP' },
  { label: 'Ready to Ship', value: 'NO_READY_TO_SHIP' },
  { label: 'Shipped to Patient', value: 'NO_SHIPPED' },
  { label: 'Delivered', value: 'NO_DELIVERED' },
  { label: 'Pending Return', value: 'PENDING_RETURN' },
  { label: 'Shipped to TestSmarter', value: 'NO_SHIPPED_BACK' },
]

const followUpCareStatusOptions = [
  { label: 'New', value: 'FOLLOW_UP_CARE_NEW' },
  { label: 'Sent', value: 'FOLLOW_UP_CARE_SENT' },
  { label: 'Confirmed', value: 'FOLLOW_UP_CARE_CONFIRMED' },
]

const agingOrder = ['0-14', '15-30', '31-60', '60-120', '120+']

type AgingRow = { range: string; count: number }

export const PatientAgingReport = () => {
  const [type, setType] = useState<PatientAgingType>('HST')
  const [status, setStatus] = useState<string>('HST_OPEN')

  useEffect(() => {
    if (type === 'FOLLOW_UP_CARE') setStatus('FOLLOW_UP_CARE_NEW')
  }, [type])
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const navigate = useNavigate()

  const statusOptions = useMemo(() => {
    if (type === 'HST') return hstStatusOptions
    if (type === 'FOLLOW_UP_CARE') return followUpCareStatusOptions
    return oneShipStatusOptions
  }, [type])

  useEffect(() => {
    if (!statusOptions.find((option) => option.value === status) && statusOptions.length) {
      setStatus(statusOptions[0].value)
    }
  }, [statusOptions, status])

  useEffect(() => {
    setCurrentPage(0)
  }, [type, status])

  const { data, isFetching } = useGetPatientAgingReportQuery(
    type && status ? { type, status, page: currentPage, size: pageSize } : skipToken,
  )

  const [exportPatientAgingReport, { isLoading: isExporting }] = useExportPatientAgingReportMutation()

  const handleExport = useCallback(async () => {
    const blob = await exportPatientAgingReport({
      type,
      status,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).unwrap()
    downloadBlob(blob, `patient-aging-report-${moment().format('YYYY-MM-DD')}.csv`)
  }, [type, status, exportPatientAgingReport])

  const bucketRows: AgingRow[] = useMemo(() => {
    if (!data) return []
    return agingOrder.map((range) => ({
      range,
      count: data.buckets.find((bucket) => bucket.range === range)?.count || 0,
    }))
  }, [data])

  const allStatusOptions = useMemo(
    () => [...hstStatusOptions, ...oneShipStatusOptions, ...followUpCareStatusOptions],
    [],
  )

  const statusLabel = useCallback(
    (bucket: string) => allStatusOptions.find((opt) => opt.value === bucket)?.label || bucket,
    [allStatusOptions],
  )

  const tableColumns = useMemo(
    () => [
      { Header: 'Order ID', accessor: 'id', className: 'text-nowrap' },
      { Header: 'Patient', accessor: 'patientName' },
      { Header: 'Test Type', accessor: 'testType' },
      { Header: 'Office', accessor: 'officeName' },
      {
        Header: 'Status',
        accessor: 'bucketLabel',
      },
      {
        Header: 'Aging (days)',
        accessor: 'agingDays',
      },
      {
        Header: 'Status Changed',
        accessor: 'labBucketChangedAt',
        Cell: ({ value }) => (value ? moment(value).format('MM/DD/YYYY') : '—'),
        className: 'text-nowrap',
      },
    ],
    [],
  )

  const tableData = useMemo(() => {
    const items =
      data?.orders?.items?.map((item) => ({
        ...item,
      bucketLabel: statusLabel(String(item.bucket)),
      })) || []

    return {
      items,
      currentPage: data?.orders?.currentPage ?? currentPage,
      totalItems: data?.orders?.totalItems ?? 0,
    }
  }, [data, currentPage, statusLabel])

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <Card className="mt-3">
            <Card.Header>
              <div className="d-flex flex-wrap align-items-center">
                <h4 className="mb-0 mr-3">Patient Aging by Status</h4>
                {isFetching && <Spinner animation="border" size="sm" />}
              </div>
            </Card.Header>
            <Card.Body>
              <div className="row mb-3">
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Type</label>
                  <Select
                    classNamePrefix="react-select"
                    value={typeOptions.find((option) => option.value === type)}
                    onChange={(option) => option && setType(option.value)}
                    options={typeOptions}
                  />
                </div>
                <div className="col-md-4 col-sm-12 mb-2">
                  <label className="mb-1">Status</label>
                  <Select
                    classNamePrefix="react-select"
                    value={statusOptions.find((option) => option.value === status) || null}
                    onChange={(option) => option && setStatus(option.value)}
                    options={statusOptions}
                  />
                </div>
              </div>

              <Table responsive>
                <thead>
                  <tr>
                    <th>Aging Bucket</th>
                    <th className="text-right">Patients</th>
                  </tr>
                </thead>
                <tbody>
                  {bucketRows.map((row) => (
                    <tr key={row.range}>
                      <td>{row.range} days</td>
                      <td className="text-right">{row.count}</td>
                    </tr>
                  ))}
                  <tr className="font-weight-bold">
                    <td>Total</td>
                    <td className="text-right">{data?.total || 0}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <Card className="mt-3">
            <Card.Header>
              <div className="d-flex flex-wrap align-items-center">
                <h4 className="mb-0 mr-3">Orders</h4>
                {(isFetching || isExporting) && <Spinner animation="border" size="sm" />}
                <Button
                  variant="outline-success"
                  className="ml-auto"
                  onClick={handleExport}
                  disabled={isExporting || !tableData.totalItems}>
                  Export CSV
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <TableWrapper className="card-body row">
                <DataTable
                  tableKey="patient_aging_orders"
                  data={tableData}
                  status={isFetching}
                  tableColumns={tableColumns}
                  pageSize={pageSize}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                  setPageSize={setPageSize}
                  setSortOptions={() => {}}
                  searchable={false}
                  entityName="orders"
                  onRowClick={(row) => {
                    navigate(`/lab/orderEdit/${row.original.id}`)
                  }}
                />
              </TableWrapper>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default PatientAgingReport
